/**
 * "Someone is refreshing this brand right now."
 *
 * An update rebuilds the affiliate list for a DOMAIN, and that list is shared
 * (affiliate_clean matches on brand_url_array). So when the admin re-runs
 * rainbet, every account holding rainbet — as their own brand, as a competitor,
 * or as one half of an affiliate gap — has its numbers rebuilt at that moment,
 * even though the brand_jobs row belongs to somebody else's brand id.
 *
 * The per-card polls can't show that: each one joins brand_jobs on its own
 * brand id. `GET /api/brand-updates/active` answers the domain-level question
 * instead, and this module is the single client for it — ONE request no matter
 * how many cards are on screen, because every card subscribes to the same
 * poller rather than starting its own.
 *
 * Cards read it with `useBrandUpdate(url, onFinished)`. When a brand drops out
 * of the response its run has ended, which is the cue to refetch: the new
 * affiliate numbers are already in the database by then.
 */

import { useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// While something is running the badge should feel live; while nothing is, this
// is a background "has anything started?" check and can be lazy.
const ACTIVE_POLL_MS = 10000;
const IDLE_POLL_MS = 30000;

/** Same flattening the SQL side applies, so both agree on what one domain is. */
export const normalizeBrandUrl = (url) =>
  String(url || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const subscribers = new Set();
let byUrl = new Map();
let timer = null;
let fetching = false;

const notify = () => {
  subscribers.forEach((listener) => {
    try {
      listener(byUrl);
    } catch (err) {
      console.error('brand-update subscriber failed:', err);
    }
  });
};

const schedule = () => {
  clearTimeout(timer);
  if (subscribers.size === 0) return;
  timer = setTimeout(poll, byUrl.size > 0 ? ACTIVE_POLL_MS : IDLE_POLL_MS);
};

async function poll() {
  if (fetching || subscribers.size === 0) return;

  // A hidden tab is nobody's live view. Keep the loop alive but skip the work.
  if (typeof document !== 'undefined' && document.hidden) {
    schedule();
    return;
  }

  fetching = true;
  try {
    const response = await fetch(`${API_BASE}/api/brand-updates/active`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();

    const next = new Map();
    if (data?.success && Array.isArray(data.updates)) {
      data.updates.forEach((update) => {
        next.set(normalizeBrandUrl(update.brandUrl), update);
      });
    }
    byUrl = next;
    notify();
  } catch (err) {
    // Only a badge depends on this. A failed poll leaves the last known state
    // in place and tries again — it must never surface as an error to anyone.
    console.debug('brand-update poll failed:', err.message);
  } finally {
    fetching = false;
    schedule();
  }
}

/**
 * Watch every in-flight update. Returns the unsubscribe function.
 * The first subscriber starts the poller; the last one to leave stops it.
 */
export const subscribeToBrandUpdates = (listener) => {
  subscribers.add(listener);
  listener(byUrl);

  if (subscribers.size === 1) poll();

  return () => {
    subscribers.delete(listener);
    if (subscribers.size === 0) {
      clearTimeout(timer);
      timer = null;
    }
  };
};

/**
 * Only re-render a card when something it actually shows has moved. No stage
 * here because the response carries none — cards never name a pipeline step.
 */
const signature = (update) =>
  update ? `${update.jobId}|${update.status}|${update.percent}` : '';

/**
 * The update running against one brand's domain, or null.
 *
 * @param {string} brandUrl the card's domain, in any form
 * @param {Function} [onFinished] called once when the run ends — refetch here,
 *        the rebuilt numbers are already stored
 */
export const useBrandUpdate = (brandUrl, onFinished) => {
  const key = normalizeBrandUrl(brandUrl);
  const [update, setUpdate] = useState(null);

  // Held in a ref so a parent that passes an inline arrow doesn't resubscribe
  // (and so re-poll) on every one of its own renders.
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  useEffect(() => {
    if (!key) return undefined;

    let wasRunning = false;
    let lastSignature = '';

    return subscribeToBrandUpdates((map) => {
      const next = map.get(key) || null;

      const nextSignature = signature(next);
      if (nextSignature !== lastSignature) {
        lastSignature = nextSignature;
        setUpdate(next);
      }

      // Present, then gone — the run ended. Fires once per run, and only for a
      // card that actually watched it happen, so a card mounted after the fact
      // never triggers a refetch it has no reason to.
      if (next) {
        wasRunning = true;
      } else if (wasRunning) {
        wasRunning = false;
        if (finishedRef.current) finishedRef.current();
      }
    });
  }, [key]);

  return update;
};

/**
 * Fires when ANY brand's run ends.
 *
 * For a list that refetches a page at a time: one refetch however many runs
 * finished, and the caller decides what "refetch" means — a paginated dashboard
 * must not be reloaded by a card that has no idea which page it is on.
 */
export const useBrandUpdatesFinished = (onFinished) => {
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  useEffect(() => {
    let previous = new Set();

    return subscribeToBrandUpdates((map) => {
      const current = new Set(map.keys());
      const ended = [...previous].some((url) => !current.has(url));
      previous = current;
      if (ended && finishedRef.current) finishedRef.current();
    });
  }, []);
};
